import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "next-i18next";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { Container } from "~/components/Container";
import Icon from "~/components/Icon";
import { IconNames } from "~/components/Icon/Icon.type";
import Colors from "~/constants/colors";
import Trans from "~/types/translationKeys";
import styles from "./SeeCXGenieInAction.module.scss";

const actions = [
  {
    name: "Sale Automation",
    icon: "bag-2",
    url: "/videos/sale-automation.webm",
    description:
      "CX Genie can learn your product list and recommend the accurate product match your customer need",
  },
  {
    name: "Collect Lead Automation",
    icon: "clipboard-text",
    url: "/videos/collect-lead-collection.webm",
    description:
      "CX Genie's Flow Builder lets you create powerful, branching conversations that guide visitors, capture leads, and deliver personalized responses. Turn every chat into a qualified lead, 24/7.",
  },
  {
    name: "Customer Support Automation",
    icon: "chatbot",
    url: "/videos/customer-support-automation.webm",
    description:
      "Automates your customer support by managing tickets seamlessly and collecting valuable feedback. Deliver fast, consistent service that scales with your business no matter the size.",
  },
  {
    name: "Multi-channel Support",
    icon: "element-plus",
    url: "/videos/multi-channel-support.webm",
    description:
      "CX Genie brings together your website, social media (Facebook, Instagram, Whatsapp,…), chat apps like Slack, Telegram, Discord and email so you can manage and reply to all customer messages quickly and efficiently from a single platform.",
  },
  {
    name: "Customer Support Team Performance",
    icon: "people",
    url: "/videos/customer-support-team-performance.webm",
    description:
      "Manage customer support effortlessly with clear CSAT reports and a streamlined helpdesk system. Spend less time on admin and more time delivering outstanding service.",
  },
];

const SeeCXGenieInAction: FC = () => {
  const { t } = useTranslation();
  const [activeAction, setActiveAction] = useState(0);
  const [progress, setProgress] = useState(0);
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const [videoRetry, setVideoRetry] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Intersection Observer để detect khi section vào viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.3, // Trigger khi 30% section hiển thị
        rootMargin: "0px 0px -100px 0px", // Trigger sớm hơn 100px
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRefs.current[activeAction];
    if (!video) {
      setTimeout(() => {
        setVideoRetry((prev) => !prev);
      }, 300);
      return;
    }

    const handleLoadedMetadata = () => {
      video.currentTime = 0;
      setProgress(0);
      if (isInView) {
        video.play();
      }
    };

    const handleTimeUpdate = () => {
      const _progress = (video.currentTime / video.duration) * 100;
      setProgress(_progress);

      if (_progress >= 100) {
        setTimeout(() => {
          setActiveAction((prev) => {
            const next = (prev + 1) % actions.length;
            setTimeout(() => {
              tabRefs.current[next]?.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
              });
            }, 100);
            return next;
          });
          setProgress(0);
        }, 500);
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);

    video.load(); // Force reload!

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [activeAction, videoRetry, isInView]);

  // Handle play/pause khi isInView thay đổi
  useEffect(() => {
    const video = videoRefs.current[activeAction];
    if (!video) return;

    if (isInView) {
      video.play().catch(() => {
        // Ignore autoplay errors
      });
    } else {
      video.pause();
    }
  }, [isInView, activeAction]);

  return (
    <section ref={sectionRef} className={styles.see_cx_genie_in_action}>
      <Container className="flex flex-col gap-y-6 gap-x-20 items-end h-full md:flex-row">
        <div className={styles.left}>
          <div className="flex flex-col gap-3 items-center md:items-start">
            <h2 className={styles.title} data-aos="fade-down">
              See CX Genie in action
            </h2>
            <p className={styles.description} data-aos="fade-up">
              Automation and all the way CX Genie can transform your business.
            </p>
          </div>

          <div className={styles.actions}>
            {actions.map((action, index) => {
              const active = activeAction === index;

              return (
                <div
                  key={index}
                  ref={(el) => (tabRefs.current[index] = el)}
                  className={classNames(styles.action, {
                    [styles.active]: active,
                  })}
                  onClick={() => {
                    if (index === activeAction) return;
                    setActiveAction(index);
                    setProgress(0);
                    setTimeout(() => {
                      tabRefs.current[index]?.scrollIntoView({
                        behavior: "smooth",
                        inline: "center",
                        block: "nearest",
                      });
                    }, 100);
                  }}
                >
                  {active && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                    >
                      <Icon
                        name={action.icon as IconNames}
                        size={32}
                        className={styles.icon}
                      />
                    </motion.div>
                  )}
                  <p className={styles.text}>{action.name}</p>
                  {active && (
                    <Fragment>
                      <Icon
                        name="play-cricle"
                        size={40}
                        color={Colors.GREY_50}
                      />
                      <div className={styles.progress_bar}>
                        <div
                          className={styles.progress_thumb}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </Fragment>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={`${actions[activeAction].url}-${activeAction}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className={styles.right}
          >
            <video
              key={`${actions[activeAction].url}-${activeAction}`}
              ref={(el) => {
                videoRefs.current[activeAction] = el!;
              }}
              src={actions[activeAction].url}
              autoPlay
              muted
              playsInline
              preload="auto"
              className={styles.video}
            >
              {t(Trans.your_browser_does_not_support_the_video_tag)}
            </video>
            <p className={styles.description}>
              {actions[activeAction].description}
            </p>
          </motion.div>
        </AnimatePresence>
      </Container>
    </section>
  );
};

export default SeeCXGenieInAction;
